import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ITrigger,
  ITriggerAction,
  ITriggerSetAction,
} from 'app/services/api5-service/api.interface';
import { Observable, Subject, merge, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import { TriggerSetEvent } from './model';

import { IFormModel } from '../../dynamic-form.interface';
import { FormService, ValueChangeWithControl } from '../form.service';

/**
 * Trigger service. Watch for events on form, like change, blur or focus, and perform some actions, depending of BE metadata
 */
@UntilDestroy()
@Injectable()
export class TriggerService {
  private readonly triggerEvent: Subject<TriggerSetEvent> = new Subject();

  constructor(private readonly formService: FormService) {
    this.watchForDocChange();
  }

  /**
   * Get array of control names, that match some regexp string
   *
   * @param regexp - string, that will be used as regexp
   * @param formGroup
   */
  private getMatchedControls(regexp: string, formGroup: FormGroup): string[] {
    const regexpTest = new RegExp(regexp);
    return Object.entries(formGroup.controls)
      .filter(([controlName]) => regexpTest.test(controlName))
      .map(([controlName]) => controlName);
  }

  private watchForDocChange(): void {
    this.formService.doc$
      .pipe(
        map(doc => DocHelper.getArrayWrapperValue(doc.metadata?.form?.trigger)),
        switchMap(triggers => {
          if (!triggers) {
            return of();
          }

          return this.handleTriggers$(triggers);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Handle triggers - watch for conditions and perform actions
   *
   * @param triggers - triggers metadata
   */
  private handleTriggers$(triggers: ITrigger[]): Observable<void> {
    const triggersHandling = triggers.map(trigger => {
      if (trigger.$type === 'change') {
        return this.handleChangeTrigger$(trigger);
      } else {
        // @TODO i.ablov blur/focus triggers
        return of();
      }
    });

    return merge(...triggersHandling);
  }

  /**
   * Handle change trigger. Watch for value change and perform action
   *
   * @param trigger - change trigger metadata
   */
  private handleChangeTrigger$(trigger: ITrigger): Observable<void> {
    const triggerActions = DocHelper.getArrayWrapperValue(trigger.then);

    return this.formService.valueChangesOnlyWithControl$.pipe(
      map((changes: ValueChangeWithControl) => {
        const triggerChanges = Object.keys(changes);

        const regexp = new RegExp(trigger.$source);
        return triggerChanges.filter(key => regexp.test(key));
      }),
      filter(triggerSourcesNames => triggerSourcesNames.length > 0),
      tap(triggerSourcesNames => {
        triggerSourcesNames.forEach(() => {
          this.handleTriggerActions(triggerActions);
        });
      }),
      map(() => undefined),
    );
  }

  /**
   * Handle trigger actions after some condition meet
   *
   * @param triggerActions - trigger actions metadata
   */
  private handleTriggerActions(triggerActions: ITriggerAction[]): void {
    triggerActions.forEach(action => {
      if (action.set) {
        const setActions = DocHelper.getArrayWrapperValue(action.set);
        this.handleTriggerSetAction(setActions);
      }
    });
  }

  /**
   * Handler trigger set action. Set some new value to other form control on form
   *
   * @param actions - trigger set actions metadata
   */
  private handleTriggerSetAction(actions: ITriggerSetAction[]): void {
    const formGroup = this.formService.formGroup;
    if (!formGroup) {
      return;
    }

    actions.forEach(action => {
      const actionTargets = this.getMatchedControls(action.$target, formGroup);

      actionTargets.forEach(target => {
        const valueToSet = this.getValueForSetAction(action);

        if (valueToSet !== undefined) {
          this.triggerEvent.next({
            controlName: target,
            value: valueToSet,
          });
        }
      });
    });
  }

  /**
   * Get value for trigger set action depending on metadata
   *
   * @param action - set action metadata
   */
  private getValueForSetAction(
    action: ITriggerSetAction,
  ): IFormModel[string] | undefined {
    if (typeof action.$from === 'string') {
      return this.formService.formControl(action.$from)?.value;
    }

    if (typeof action.$value === 'string') {
      return action.$value;
    }

    return undefined;
  }

  /**
   * Get stream of trigger events for formControl by name
   *
   * @param controlName
   */
  getTriggerSetEvent$(controlName: string): Observable<IFormModel[string]> {
    return this.triggerEvent.pipe(
      filter(event => event.controlName === controlName),
      map(event => event.value),
    );
  }
}
